import './App.css'
import React, {useEffect, useState} from 'react'
import {URL_API_USER_TRACK} from './utils/Constants'
import RequestSvc, { tokenValidationSvc } from './services/RequestSvc'
import SearchView from "./views/SearchView";
import OfferView from "./components/OffersMain/offerview";
import UserVaultView from "./views/UserVaultView";
import HeaderComponent from "./components/toolbars/HeaderComponent";
import SidebarComponent from "./components/sidebars/SidebarComponent";
import {createTheme, ThemeProvider} from '@mui/material/styles';
import getStoredValue, {UseLocalStorage} from './services/UseLocalStorage'
import UserAsk from './components/IASearch/UserAsk';
import IASearchView from './components/IASearch/IASearchView';
import ShowContentForUser from "./components/modalwindow/ShowContentForUser";
import PagesView from "./views/PagesView";
import { Button } from '@mui/material';

// Cache busting utility
const clearAllCaches = async () => {
    try {
        // Clear all cache storage
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
            console.log('All caches cleared');
        }
        
        // Clear localStorage except for auth token
        const authToken = localStorage.getItem('P-CS');
        localStorage.clear();
        if (authToken) {
            localStorage.setItem('P-CS', authToken);
        }
        
        // Clear sessionStorage
        sessionStorage.clear();
        
    } catch (error) {
        console.warn('Error clearing caches:', error);
    }
};

// Version detection and cache clearing
const checkAndClearOldVersion = () => {
    const APP_VERSION = process.env.REACT_APP_VERSION || Date.now().toString();
    const STORED_VERSION = localStorage.getItem('app-version');
    
    if (STORED_VERSION && STORED_VERSION !== APP_VERSION) {
        console.log('New version detected, clearing caches...');
        clearAllCaches();
        localStorage.setItem('app-version', APP_VERSION);
        // Force reload to get fresh files
        alert('Se ha detectado una nueva versión de la aplicación. La página se recargará para actualizar los recursos.');
        window.location.reload(true);
        return true;
    }
    
    if (!STORED_VERSION) {
        localStorage.setItem('app-version', APP_VERSION);
    }
    
    return false;
};

// Add no-cache headers dynamically
const addNoCacheHeaders = () => {
    // Add meta tags to prevent caching
    const metaTags = [
        { httpEquiv: 'Cache-Control', content: 'no-cache, no-store, must-revalidate' },
        { httpEquiv: 'Pragma', content: 'no-cache' },
        { httpEquiv: 'Expires', content: '0' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' }
    ];
    
    metaTags.forEach(tag => {
        const existingTag = document.querySelector(
            `meta[${tag.httpEquiv ? 'http-equiv' : 'name'}="${tag.httpEquiv || tag.name}"]`
        );
        
        if (!existingTag) {
            const meta = document.createElement('meta');
            if (tag.httpEquiv) {
                meta.setAttribute('http-equiv', tag.httpEquiv);
            } else {
                meta.setAttribute('name', tag.name);
            }
            meta.setAttribute('content', tag.content);
            document.head.appendChild(meta);
        }
    });
};

const modalContent = (
    <iframe
        width="600"
        height="440"
        src="https://www.youtube.com/embed/vZfkkBnm3EA?si=GYMIkyiMPvZN3NC5"

        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
    ></iframe>
);

const theme = createTheme({
    typography: {
        "fontFamily": `"Montserrat", "sans-serif"`,
    }
});

async function addTrack() {
    try {
        const queryParameters = new URLSearchParams(window.location.search)
        const src = queryParameters.get('src') ?? ''
        let url = URL_API_USER_TRACK + `?evento=Profilet&src=${src}&t=${Date.now()}`
        let requestSvc = new RequestSvc()
        let result = await requestSvc.get(url)
        if (result.status_code === 401) {
        //    window.location.href = 'https://app.pulpoia.com/login';
        }
    } catch (error) {
        console.error('Error in addTrack:', error);
    }
}

function App() {
    const [showModal, setShowModal] = useState(true);
    const [isAppReady, setIsAppReady] = useState(false);
    const [showUpdateNotification, setShowUpdateNotification] = useState(false); // State for the update notification

    // Detect stale cache and show update notification
    useEffect(() => {
        const handleResourceError = (event) => {
            const error = event.reason || event;
            const errorMessage = error.message || '';
            const isChunkLoadError = /Loading( CSS)? chunk .* failed/i.test(errorMessage);

            if (isChunkLoadError) {
                // Instead of reloading, show a notification to the user
                console.log('Stale version detected. Prompting user to update.');
                setShowUpdateNotification(true);
            }
        };

        window.addEventListener('unhandledrejection', handleResourceError);

        return () => {
            window.removeEventListener('unhandledrejection', handleResourceError);
        };
    }, []);
    
    const handleCloseModal = () => {
        setShowModal(false);
        // Ensure content is visible after modal closes
        if (isFirstLoad) {
            setIsAppReady(true);
        }
    };

    // Parse query string and manage auth token in localStorage
    const queryParameters = new URLSearchParams(window.location.search);
    const token = queryParameters.get('token');
    
    const [tcsToken, setTcsToken] = UseLocalStorage('P-CS', token || '');

    // Ensure token presence logic and redirects
    useEffect(() => {
        const validateAndSetupToken = async () => {
            try {
                const existing = getStoredValue('P-CS');
                
                if (token) {
                    // If token is in query and no stored token, persist it
                    if (!existing || existing === '') {
                        setTcsToken(token);
                    }
                    //delete token from the querystring
                    queryParameters.delete('token');
                    window.history.replaceState({}, '', `${window.location.pathname}?${queryParameters}`);
                    
                    // Validate the new token from query
                    console.log('Validating token from query string...');
                    await tokenValidationSvc.validateAndRedirect();
                } else {
                    // No token in query: require existing stored token or redirect
                    if (!existing || existing === '') {
                        window.location.href = 'https://app.pulpoia.com/login';
                        return;
                    }
                    
                    // Validate existing stored token
                    console.log('Validating existing stored token...');
                    const isValid = await tokenValidationSvc.validateAndRedirect();
                    if (!isValid) {
                        // Token was invalid, validateAndRedirect already handled redirect
                        return;
                    }
                }
                
                // After successful validation, mark app as ready if modal is not showing
                if (!showModal) {
                    setIsAppReady(true);
                }
            } catch (error) {
                console.error('Error in token validation:', error);
                // On error, try to clear cache and reload
                clearAllCaches().then(() => {
                    window.location.reload(true);
                });
            }
        };
        
        validateAndSetupToken();
        // We intentionally run this once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Gets the referrer information and module name
     * @returns {string}
     */
    const getModuleName = () => {
        try {
            const url = window.location.href.toLowerCase();
            const referrer = document.referrer.toLowerCase();
            
            // Check for direct clicspy.com URLs first
            if (url.includes('pulpoia.com/dashboard') || 
                url === 'http://pulpoia.com' || 
                url === 'https://pulpoia.com') {
                return 'dashboard';
            }
            
            // Check referrer if available
            if (referrer && referrer.includes('pulpoia.com')) {
                const referrerPath = new URL(referrer).pathname;
                if (referrerPath === '/' || referrerPath === '/dashboard') {
                    return 'dashboard';
                }
            }
            
            return url.substring(url.lastIndexOf('/') + 1);
        } catch (error) {
            console.error('Error in getModuleName:', error);
            return 'dashboard';
        }
    };

    // State to manage the selected offer subitem
    const [selectedOfferSubitem, setSelectedOfferSubitem] = useState(null);

    const moduleName = getModuleName();
    /**
     * Flag  que indica y almacena el estado del sidebar
     */
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    /**
     * Flag que indica si se esta mostrando la grilla de vault o no.
     */
    const [showVaultGrid, setShowVaultGrid] = useState(moduleName === 'dashboard');
    const [fk_categoryCode, setFk_categoryCode] = useState(null);
    const [filterName, setFilterName] = useState(null);
    const [newRow, setNewRow] = useState({});
    const [refreshVaultTrigger, setRefreshVaultTrigger] = useState(0);
    // Show OfferView on first page load (per reload). It will hide after any navigation.
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    // Show Pulpo IA view flag
    const [showIASearch, setShowIASearch] = useState(false);
    // Transition states for smooth UserAsk to IASearchView transition
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showUserAsk, setShowUserAsk] = useState(true);
    const [lastUserQuery, setLastUserQuery] = useState('');
    // Clone pages view flag
    const [showClonePages, setShowClonePages] = useState(false);

    const setCategoryCodeSelected = (catCodeValue = null, filterNameValue = null) => {
        setFk_categoryCode(catCodeValue);
        setFilterName(filterNameValue)
    }

    // Handle smooth transition from UserAsk to IASearchView
    const handleUserAskSubmit = async (searchQuery) => {
        setIsTransitioning(true);
        setLastUserQuery(searchQuery);
        
        // Fade out UserAsk (300ms)
        setShowUserAsk(false);
        
        // Wait for fade out, then fade in IASearchView
        setTimeout(() => {
            setShowIASearch(true);
            setIsTransitioning(false);
        }, 300);
    };

    // Reset UserAsk visibility when navigating away from IA search
    useEffect(() => {
        if (!showIASearch) {
            setShowUserAsk(true);
            setLastUserQuery('');
        }
    }, [showIASearch]);


    useEffect(() => {
        addTrack()
    }, [])

    // Hide OfferView only when the user navigates from the sidebar
    const handleFirstNavigation = () => {
        if (isFirstLoad) setIsFirstLoad(false);
    };

    // Forzar barra lateral extendida cuando se selecciona un nicho/oferta
    useEffect(() => {
        if (selectedOfferSubitem) {
            setSidebarOpen(true);
        }
    }, [selectedOfferSubitem]);

    // Don't render main content until app is ready and modal is closed
    if (!isAppReady || showModal) {
        return (
            <ThemeProvider theme={theme}>
                <div>
                    {showModal && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1301 // Higher than other elements like MUI components
                        }}>
                            <ShowContentForUser onClose={handleCloseModal} myIframe={modalContent} width={640} height={480}/>
                        </div>
                    )}
                    {!showModal && !isAppReady && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            height: '100vh',
                            backgroundColor: 'white',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            Loading...
                        </div>
                    )}
                </div>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            {/* Update Notification Banner */}
            {showUpdateNotification && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    backgroundColor: '#ffc107',
                    color: 'black',
                    textAlign: 'center',
                    padding: '10px',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    A new version of the application is available.
                    <Button 
                        variant="contained" 
                        onClick={() => window.location.reload(true)}
                        style={{ marginLeft: '15px', backgroundColor: 'black', color: 'white' }}
                    >
                        Refresh
                    </Button>
                </div>
            )}
            <div>
                <HeaderComponent isSidebarOpen={isSidebarOpen} setSidebarOpen={setSidebarOpen}/>
                <div className={"flex-box-content"}>
                    <SidebarComponent isSidebarOpen={isSidebarOpen}
                                      setSidebarOpen={setSidebarOpen}
                                      showVaultGrid={showVaultGrid}
                                      setShowVaultGrid={setShowVaultGrid}
                                      selectedOfferSubitem={selectedOfferSubitem}
                                      setSelectedOfferSubitem={setSelectedOfferSubitem}
                                      onFirstNavigation={handleFirstNavigation}
                                      setShowIASearch={setShowIASearch}                                      
                                      setShowClonePages={setShowClonePages}
                                      />
                    {
                        <div className="search-view-main-container">
                            <div style={{display: isFirstLoad ? 'block' : 'none'}}>
                                <OfferView
                                    isSidebarOpen={isSidebarOpen}
                                    setSidebarOpen={setSidebarOpen}
                                    setNewRow={setNewRow}
                                    selectedOfferSubitem={selectedOfferSubitem}
                                />
                            </div>
                            <div style={{display: !isFirstLoad && showVaultGrid ? 'block' : 'none'}}>
                                <UserVaultView isSidebarOpen={isSidebarOpen}
                                               setSidebarOpen={setSidebarOpen}
                                               fk_categoryCode={fk_categoryCode}
                                               filterName={filterName}
                                               setCategoryCodeSelected={setCategoryCodeSelected}
                                               newRow={newRow}
                                               refreshVaultTrigger={refreshVaultTrigger}
                                />
                            </div>
                            <div style={{display: !isFirstLoad && !showVaultGrid && !showIASearch && !showClonePages ? 'block' : 'none'}}>
                                <SearchView
                                    isSidebarOpen={isSidebarOpen}
                                    setSidebarOpen={setSidebarOpen}
                                    setNewRow={setNewRow}
                                    setRefreshVaultTrigger={setRefreshVaultTrigger}
                                    selectedOfferSubitem={selectedOfferSubitem}
                                />
                            </div>
                            <div style={{display: !isFirstLoad && showIASearch ? 'block' : 'none'}}>
                                {showUserAsk && (
                                    <UserAsk onSubmit={handleUserAskSubmit} />
                                )}
                                {showIASearch && !showUserAsk && (
                                    <IASearchView 
                                        initialQuery={lastUserQuery} 
                                        fadeIn={!isTransitioning} 
                                    />
                                )}
                            </div>
                            <div style={{display: !isFirstLoad && showClonePages ? 'block' : 'none'}}>
                                {showClonePages && <PagesView />}
                            </div>
                        </div>
                    }
                </div>
            </div>
        </ThemeProvider>
    )
}

export default App
